import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Copy, Edit, ArrowRight, Rocket, LayoutList, Columns, MessageSquare } from "lucide-react";
import { useCampaigns, type CampaignSummary, type CampaignDetail } from "@/shared/api/wb";
import { api } from "@/shared/api/client";
import { useDraftStore } from "@/shared/store/draft";
import { fmtNum, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import { EmptyState, Skeleton } from "@/shared/ui";
import { InboxModal } from "./InboxModal";

const CAMP_TYPE = {
  seeding: { label: "Посев", icon: Copy },
  native:  { label: "Натив", icon: Edit },
};

const CAMP_STATUS = {
  active:     { label: "Идёт",      bg: "var(--status-pub-bg)",    ink: "var(--status-pub-ink)"    },
  moderation: { label: "Модерация", bg: "var(--status-agree-bg)",  ink: "var(--status-agree-ink)"  },
  done:       { label: "Завершена", bg: "var(--status-results-bg)",ink: "var(--status-results-ink)"},
  draft:      { label: "Черновик",  bg: "var(--status-draft-bg)",  ink: "var(--status-draft-ink)"  },
};

const STATUS_RANK: Record<string, number> = { active: 0, moderation: 1, draft: 2, done: 3 };

function sortCampaigns(campaigns: CampaignSummary[]): CampaignSummary[] {
  return [...campaigns].sort((a, b) => {
    const rankA = STATUS_RANK[a.status] ?? 9;
    const rankB = STATUS_RANK[b.status] ?? 9;
    if (rankA !== rankB) return rankA - rankB;
    // within same status: is_current first (backend already does this, preserve order)
    return 0;
  });
}

type Filter  = "all" | "seeding" | "native";
type ViewMode  = "list" | "kanban";
type SortOrder = "date" | "status";

export function CampaignsPage() {
  const { data, isLoading } = useCampaigns();
  const [filter,    setFilter]    = useState<Filter>("all");
  const [viewMode,  setViewMode]  = useState<ViewMode>("list");
  const [sortOrder, setSortOrder] = useState<SortOrder>("date");
  const all = data ?? [];

  const base     = all.filter((c) => filter === "all" || c.type === filter);
  const filtered = sortOrder === "status" ? sortCampaigns(base) : base;
  const seedingCount = all.filter((c) => c.type === "seeding").length;
  const nativeCount  = all.filter((c) => c.type === "native").length;

  if (isLoading) return <CampaignsSkeleton />;

  return (
    <div className="p-7 pb-16">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-extrabold tracking-tight text-[28px] leading-tight">Мои РК</h1>
        {all.length > 0 && (
          <Link to="/campaign/new"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-semibold text-[14px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]">
            <Plus size={16} strokeWidth={3} /> Новая РК
          </Link>
        )}
      </div>

      {all.length === 0 ? (
        <EmptyState
          icon={<Rocket size={28} strokeWidth={1.8} />}
          title="Запустите первую рекламную кампанию"
          description={<>
            WOWBLOGGER подберёт каналы под продукт, оформит креативы с маркировкой
            и приведёт реальные просмотры. На запуск уйдёт <b>около 5 минут</b>.
          </>}
          primaryCta={{ label: "Создать РК", to: "/campaign/new" }}
          secondaryLink={{ label: "Открыть каталог блогеров", to: "/catalog" }}
        />
      ) : (
        <>
          <div className="flex items-center justify-between mb-5">
            <div className="inline-flex p-1 rounded-pill bg-ink-6 gap-0.5">
              <FilterTab active={filter === "all"} onClick={() => setFilter("all")}>
                Все <span className="font-mono text-[11px] opacity-60 ml-1">{all.length}</span>
              </FilterTab>
              <FilterTab active={filter === "seeding"} onClick={() => setFilter("seeding")}>
                <Dot color="var(--wb-lime)" /> Посев <span className="font-mono text-[11px] opacity-60 ml-1">{seedingCount}</span>
              </FilterTab>
              <FilterTab active={filter === "native"} onClick={() => setFilter("native")}>
                <Dot color="var(--wb-pink)" /> Натив <span className="font-mono text-[11px] opacity-60 ml-1">{nativeCount}</span>
              </FilterTab>
            </div>

            <div className="flex items-center gap-2">
              {/* Sort */}
              <div className="inline-flex p-0.5 rounded-lg bg-ink-6 gap-0.5">
                <SortBtn active={sortOrder === "date"} onClick={() => setSortOrder("date")}>По дате</SortBtn>
                <SortBtn active={sortOrder === "status"} onClick={() => setSortOrder("status")}>По статусу</SortBtn>
              </div>

              {/* Divider */}
              <div className="w-px h-5 bg-ink-12" />

              {/* View toggle */}
              <div className="inline-flex p-0.5 rounded-lg bg-ink-6 gap-0.5">
                <ViewBtn active={viewMode === "list"} onClick={() => setViewMode("list")} title="Список">
                  <LayoutList size={15} />
                </ViewBtn>
                <ViewBtn active={viewMode === "kanban"} onClick={() => setViewMode("kanban")} title="Канбан">
                  <Columns size={15} />
                </ViewBtn>
              </div>
            </div>
          </div>

          {viewMode === "list"
            ? <ListView list={filtered} />
            : <KanbanView list={filtered} />}
        </>
      )}
    </div>
  );
}

function SortBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={cn("h-7 px-3 rounded-md text-[12px] font-display font-semibold transition-colors",
        active ? "bg-white text-ink shadow-sm" : "text-ink-60 hover:text-ink")}>
      {children}
    </button>
  );
}

function ViewBtn({ active, onClick, title, children }: { active: boolean; onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button onClick={onClick} title={title}
      className={cn("w-8 h-8 grid place-items-center rounded-md transition-colors",
        active ? "bg-white text-ink shadow-sm" : "text-ink-40 hover:text-ink")}>
      {children}
    </button>
  );
}

function ListView({ list }: { list: CampaignSummary[] }) {
  if (list.length === 0) return (
    <div className="text-center text-ink-40 py-14">Нет кампаний в этом фильтре.</div>
  );
  return (
    <div className="flex flex-col gap-2">
      {list.map((c) => <CampaignRow key={c.id} c={c} />)}
    </div>
  );
}

function KanbanView({ list }: { list: CampaignSummary[] }) {
  const COLS: { key: string; label: string }[] = [
    { key: "active",     label: "Активные"   },
    { key: "moderation", label: "Модерация"  },
    { key: "draft",      label: "Черновики"  },
    { key: "done",       label: "Завершённые"},
  ];
  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {COLS.map(({ key, label }) => {
        const items = list.filter((c) => c.status === key);
        const st = CAMP_STATUS[key as keyof typeof CAMP_STATUS] ?? CAMP_STATUS.draft;
        return (
          <div key={key} className="w-[300px] flex-shrink-0">
            <div className="flex items-center justify-between mb-2.5 px-0.5">
              <span className="text-[11px] font-display font-bold uppercase tracking-[0.08em]"
                style={{ color: st.ink }}>{label}</span>
              <span className="text-[11px] font-mono text-ink-40">{items.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {items.map((c) => <CampaignCard key={c.id} c={c} />)}
              {items.length === 0 && (
                <div className="rounded-md border border-dashed border-ink-12 py-8 text-center text-[12px] text-ink-30">—</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FilterTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={cn("inline-flex items-center px-3.5 py-1.5 rounded-pill text-[13px] font-display font-semibold transition-colors",
        active ? "bg-white text-ink shadow-sm" : "text-ink-60 hover:text-ink")}>
      {children}
    </button>
  );
}

function Dot({ color }: { color: string }) {
  return <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: color }} />;
}


function CampaignRow({ c }: { c: CampaignSummary }) {
  const navigate = useNavigate();
  const setActiveCampaign = useDraftStore((s) => s.setActiveCampaign);
  const setBranch = useDraftStore((s) => s.setBranch);
  const loadDraftFromServer = useDraftStore((s) => s.loadDraftFromServer);
  const [inboxOpen, setInboxOpen] = useState(false);
  const st = CAMP_STATUS[c.status as keyof typeof CAMP_STATUS] ?? CAMP_STATUS.draft;
  const TypeIcon = CAMP_TYPE[c.type].icon;
  const isDraft = c.status === "draft";
  const pct = c.budget ? Math.min(100, Math.round((c.spent / c.budget) * 100)) : 0;

  const onOpen = async () => {
    setActiveCampaign(c.id);
    setBranch("campaignType", c.type);
    if (isDraft) {
      try {
        const detail = await api<CampaignDetail>(`/api/v1/campaigns/${c.id}`);
        const blob = (detail.wizard_draft ?? {}) as { draft?: unknown; branch?: unknown };
        loadDraftFromServer({
          draft: (blob.draft as never) ?? {
            productUrl: detail.product_url ?? "", productAutofilled: false,
            productName: detail.product, productCategory: detail.product_category ?? "",
            productType: detail.product_type ?? "service", productDesc: detail.product_desc,
            geo: detail.geo, budget: detail.budget,
            selectedBloggerIds: detail.selected_blogger_ids,
            creativeChannelMap: detail.creative_channel_map,
            creative: { seedingText: "", nativeBrief: { task: "", tone: "", forbidden: "", hashtags: "" }, abVariant: null },
            variants: [], advertiserDataSaved: false,
          },
          branch: (blob.branch as never) ?? { campaignType: detail.type, bloggersTab: "recommended", budgetMode: "auto", payment: "card", kanbanView: "table"},
          draftCampaignId: detail.id,
        });
        navigate(detail.wizard_step ?? "/campaign/new/product");
      } catch (e) { console.error("resume draft failed", e); }
      return;
    }
    navigate(`/campaign/${c.id}`);
  };

  return (
    <div onClick={onOpen}
      className={cn(
        "flex items-center gap-4 px-5 py-4 rounded-md border bg-white cursor-pointer hover:border-ink/30 transition-colors",
        c.is_current ? "border-lime ring-1 ring-lime" : "border-ink-12",
      )}
    >
      {/* Type icon */}
      <div className={cn("w-9 h-9 rounded-md grid place-items-center flex-shrink-0",
        c.type === "seeding" ? "bg-lime/20" : "bg-pink/20")}>
        <TypeIcon size={15} strokeWidth={2.5} />
      </div>

      {/* Name + desc */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-[15px] tracking-tight truncate">{c.product}</span>
          {c.is_current && <span className="w-1.5 h-1.5 rounded-full bg-lime animate-pulse flex-shrink-0" />}
        </div>
        <div className="text-[12px] text-ink-40 truncate mt-0.5">{c.product_desc || c.period}</div>
      </div>

      {/* Status */}
      <span className="inline-flex items-center px-2.5 py-1 rounded-pill text-[11px] font-display font-semibold uppercase tracking-wider flex-shrink-0"
        style={{ background: st.bg, color: st.ink }}>
        {st.label}
      </span>

      {/* Metrics (only for non-draft) */}
      {!isDraft && (
        <div className="hidden lg:flex items-center gap-5 flex-shrink-0">
          <Metric label="Блогеров" val={`${c.closed_count}/${c.placements_count}`} />
          <Metric label="Охват" val={c.reach ? fmtNum(c.reach) : "—"} />
          <Metric label="CPV" val={c.cpv ? `${c.cpv}₽` : "—"} accent={!!c.cpv && c.cpv < 0.5} />
        </div>
      )}

      {/* Budget bar */}
      {c.budget > 0 && (
        <div className="w-[120px] flex-shrink-0 hidden xl:block">
          <div className="flex justify-between text-[11px] font-mono mb-1">
            <span className="text-ink-40">{pct}%</span>
            <span className="text-ink-60">{fmtRubFull(c.budget)}</span>
          </div>
          <div className="h-1 bg-ink-6 rounded-pill overflow-hidden">
            <div className="h-full rounded-pill"
              style={{ width: `${pct}%`, background: c.type === "seeding" ? "var(--wb-lime)" : "var(--wb-pink)" }} />
          </div>
        </div>
      )}

      {/* Action */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {!isDraft && (
          <button
            onClick={(e) => { e.stopPropagation(); setInboxOpen(true); }}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40 transition-colors"
          >
            <MessageSquare size={13} /> Написать
          </button>
        )}
        <div className="text-ink-40">
          {isDraft
            ? <ArrowRight size={16} className="text-ink-60" />
            : <ArrowRight size={16} />}
        </div>
      </div>

      {inboxOpen && (
        <InboxModal open onClose={() => setInboxOpen(false)} campaignId={c.id} />
      )}
    </div>
  );
}

function Metric({ label, val, accent }: { label: string; val: string; accent?: boolean }) {
  return (
    <div className="text-center">
      <div className={cn("font-display font-bold text-[14px] tabular-nums", accent && "text-success")}>{val}</div>
      <div className="text-[10px] text-ink-40 uppercase tracking-[0.04em]">{label}</div>
    </div>
  );
}

function CampaignCard({ c }: { c: CampaignSummary }) {
  const navigate = useNavigate();
  const setActiveCampaign = useDraftStore((s) => s.setActiveCampaign);
  const setBranch = useDraftStore((s) => s.setBranch);
  const loadDraftFromServer = useDraftStore((s) => s.loadDraftFromServer);
  const [inboxOpen, setInboxOpen] = useState(false);
  const st = CAMP_STATUS[c.status as keyof typeof CAMP_STATUS] ?? CAMP_STATUS.draft;
  const TypeIcon = CAMP_TYPE[c.type].icon;
  const isDraft = c.status === "draft";
  const pct = c.budget ? Math.min(100, Math.round((c.spent / c.budget) * 100)) : 0;

  const onOpen = async () => {
    setActiveCampaign(c.id);
    setBranch("campaignType", c.type);
    if (isDraft) {
      // Fetch full draft → hydrate the wizard store → route to the last step.
      try {
        const detail = await api<CampaignDetail>(`/api/v1/campaigns/${c.id}`);
        const blob = (detail.wizard_draft ?? {}) as { draft?: unknown; branch?: unknown };
        loadDraftFromServer({
          draft: (blob.draft as never) ?? {
            productUrl: detail.product_url ?? "",
            productAutofilled: false,
            productName: detail.product,
            productCategory: detail.product_category ?? "",
            productType: detail.product_type ?? "service",
            productDesc: detail.product_desc,
            geo: detail.geo,
            budget: detail.budget,
            selectedBloggerIds: detail.selected_blogger_ids,
            creativeChannelMap: detail.creative_channel_map,
            creative: { seedingText: "", nativeBrief: { task: "", tone: "", forbidden: "", hashtags: "" }, abVariant: null },
            variants: [],
            advertiserDataSaved: false,
          },
          branch: (blob.branch as never) ?? { campaignType: detail.type, bloggersTab: "recommended", budgetMode: "auto", payment: "card", kanbanView: "table"},
          draftCampaignId: detail.id,
        });
        navigate(detail.wizard_step ?? "/campaign/new/product");
      } catch (e) {
        console.error("resume draft failed", e);
      }
      return;
    }
    navigate(`/campaign/${c.id}`);
  };

  return (
    <div
      onClick={onOpen}
      className={cn(
        "rounded-md border bg-white p-5 transition-colors flex flex-col gap-4 cursor-pointer hover:border-ink/30",
        c.is_current ? "border-lime ring-1 ring-lime" : "border-ink-12",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="font-display font-bold text-[17px] tracking-[-0.01em] truncate">{c.product}</div>
            {c.is_current && <span className="w-2 h-2 rounded-full bg-lime animate-pulse" />}
          </div>
          <div className="text-[12.5px] text-ink-40 truncate">{c.product_desc}</div>
        </div>
        <div className="flex flex-col gap-1.5 items-end flex-shrink-0">
          <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-pill text-[11px] font-display font-semibold",
            c.type === "seeding" ? "bg-lime/20 text-ink" : "bg-pink/20 text-ink")}>
            <TypeIcon size={11} strokeWidth={2.5} /> {CAMP_TYPE[c.type].label}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-pill text-[10.5px] font-display font-semibold uppercase tracking-wider"
            style={{ background: st.bg, color: st.ink }}>
            ● {st.label}
          </span>
        </div>
      </div>

      {isDraft ? (
        <div className="flex items-center justify-between py-2">
          <span className="text-[13px] text-ink-40">Не запущена — продолжите настройку</span>
          <button onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink hover:border-ink">
            Продолжить <ArrowRight size={13} />
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2.5">
            <CampMetric label="Блогеров"   val={`${c.closed_count}/${c.placements_count}`} />
            <CampMetric label="Охват"      val={c.reach ? fmtNum(c.reach) : "—"} />
            <CampMetric label="CPV"        val={c.cpv ? `${c.cpv}₽` : "—"} accent={!!c.cpv && c.cpv < 0.5} />
            <CampMetric label="Токенов ОРД" val={c.creatives_count} />
          </div>
          <div>
            <div className="flex justify-between text-[12px] mb-1.5 gap-2">
              <span className="text-ink-40 truncate">{c.period} · {c.platforms.join(", ")}</span>
              <span className="font-mono font-semibold flex-shrink-0">{fmtRubFull(c.spent)} / {fmtRubFull(c.budget)}</span>
            </div>
            <div className="h-1.5 bg-ink-6 rounded-pill overflow-hidden">
              <div className="h-full rounded-pill transition-[width]"
                style={{ width: `${pct}%`, background: c.type === "seeding" ? "var(--wb-lime)" : "var(--wb-pink)" }} />
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setInboxOpen(true); }}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40 transition-colors w-full justify-center"
          >
            <MessageSquare size={13} /> Написать блогерам
          </button>
        </>
      )}

      {inboxOpen && (
        <InboxModal open onClose={() => setInboxOpen(false)} campaignId={c.id} />
      )}
    </div>
  );
}

function CampMetric({ label, val, accent }: { label: string; val: string | number; accent?: boolean }) {
  return (
    <div>
      <div className={cn("font-display font-extrabold text-[19px] tabular-nums tracking-[-0.02em] leading-none",
        accent ? "text-success" : "text-ink")}>{val}</div>
      <div className="text-[10.5px] uppercase tracking-[0.05em] text-ink-40 mt-1">{label}</div>
    </div>
  );
}

function CampaignsSkeleton() {
  return (
    <div className="p-7 pb-16">
      <div className="flex items-center justify-between mb-6">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-11 w-32 rounded-pill" />
      </div>
      <div className="flex items-center mb-5">
        <Skeleton className="h-9 w-56 rounded-pill" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-md border border-ink-12 bg-white p-5 flex flex-col gap-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-3.5 w-1/2" />
              </div>
              <div className="flex flex-col gap-1.5 items-end flex-shrink-0">
                <Skeleton className="h-5 w-16 rounded-pill" />
                <Skeleton className="h-4 w-14 rounded-pill" />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2.5">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="flex flex-col gap-1">
                  <Skeleton className="h-5 w-8" />
                  <Skeleton className="h-2.5 w-12" />
                </div>
              ))}
            </div>
            <div>
              <div className="flex justify-between mb-1.5">
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="h-3.5 w-24" />
              </div>
              <Skeleton className="h-1.5 rounded-pill" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
